// src/dtos/auth/login.dto.ts
import { z } from "zod";
var ALLOWED_STAGING_DOMAIN = "eumimo-web.pages.dev";
var LOGIN_REQUEST_SCHEMA = z.object({
  email: z.email(),
  originUrl: z.string().url().refine((url) => {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.hostname.endsWith(ALLOWED_STAGING_DOMAIN);
    } catch {
      return false;
    }
  }, {
    message: `Origin URL must be from ${ALLOWED_STAGING_DOMAIN} domain`
  }).optional()
});
var LOGIN_RESPONSE_SCHEMA = z.object({
  success: z.boolean(),
  message: z.string()
});

// src/dtos/auth/login-confirm.dto.ts
import { z as z2 } from "zod";
var LOGIN_CONFIRM_REQUEST_SCHEMA = z2.object({
  token: z2.string().min(1),
  code: z2.string().length(6)
});
var LOGIN_CONFIRM_RESPONSE_SCHEMA = z2.object({
  status: z2.enum([
    "success",
    "requires_confirmation"
  ]),
  accessToken: z2.string(),
  refreshToken: z2.string(),
  expiresIn: z2.number(),
  confirmationData: z2.object({
    currentPartnerName: z2.string(),
    newPartnerName: z2.string(),
    inviteToken: z2.string()
  }).optional()
});

// src/dtos/auth/pre-registration.dto.ts
import { z as z3 } from "zod";
var PRE_REGISTRATION_REQUEST_SCHEMA = z3.object({
  email: z3.email(),
  name: z3.string().min(1).max(100),
  dates: z3.array(z3.object({
    title: z3.string().min(1).max(100),
    date: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/)
  })).min(1).max(10),
  wishlist_items: z3.array(z3.object({
    title: z3.string().min(1).max(100),
    description: z3.string().max(500).optional(),
    link: z3.string().url().optional()
  })).max(20).optional(),
  // Optional: present when accepting an existing invite (guest flow)
  invite_token: z3.string().optional()
});

// src/dtos/auth/refresh-token.dto.ts
import { z as z4 } from "zod";
var REFRESH_TOKEN_REQUEST_SCHEMA = z4.object({
  refreshToken: z4.string().min(1)
});
var REFRESH_TOKEN_RESPONSE_SCHEMA = z4.object({
  accessToken: z4.string(),
  refreshToken: z4.string(),
  expiresIn: z4.number()
});

// src/dtos/internal/auth-token.dto.ts
import { z as z6 } from "zod";

// src/entities/auth-token.entity.ts
import { z as z5 } from "zod";
var AUTH_TOKEN_TYPE = z5.enum([
  "login",
  "pre_registration",
  "login_refresh"
]);
var USER_IDENTIFIER_TYPE = z5.enum([
  "email",
  "user_id"
]);
var AUTH_TOKEN_SCHEMA = z5.object({
  id: z5.string().uuid(),
  userIdentifier: z5.string(),
  userIdentifierType: USER_IDENTIFIER_TYPE,
  code: z5.string().length(6).nullable(),
  type: AUTH_TOKEN_TYPE,
  metadata: z5.unknown().nullable(),
  usedAt: z5.date().nullable(),
  expiresAt: z5.date(),
  createdAt: z5.date()
});
var PRE_REGISTRATION_METADATA_SCHEMA = z5.object({
  name: z5.string(),
  dates: z5.array(z5.object({
    title: z5.string(),
    date: z5.string()
  })),
  wishlistItems: z5.array(z5.object({
    title: z5.string(),
    description: z5.string().optional(),
    link: z5.string().optional()
  })).optional(),
  inviteToken: z5.string().optional()
});

// src/dtos/internal/auth-token.dto.ts
var CREATE_AUTH_TOKEN_INPUT_SCHEMA = z6.object({
  userIdentifier: z6.string(),
  userIdentifierType: USER_IDENTIFIER_TYPE,
  code: z6.string().length(6).nullable().optional(),
  type: AUTH_TOKEN_TYPE,
  metadata: z6.unknown().optional(),
  expiresAt: z6.date()
});

// src/dtos/internal/occasion.dto.ts
import { z as z7 } from "zod";
var CREATE_OCCASION_INPUT_SCHEMA = z7.object({
  userId: z7.string().uuid(),
  relationshipId: z7.string().uuid(),
  title: z7.string().min(1),
  date: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
});
var UPDATE_OCCASION_INPUT_SCHEMA = z7.object({
  title: z7.string().min(1).optional(),
  date: z7.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format").optional()
});

// src/dtos/internal/relationship.dto.ts
import { z as z8 } from "zod";
var CREATE_RELATIONSHIP_INPUT_SCHEMA = z8.object({
  creatorUserId: z8.string().uuid(),
  inviteToken: z8.string().optional()
});

// src/dtos/internal/subscription.dto.ts
import { z as z10 } from "zod";

// src/value-objects/company.value-object.ts
var COMPANY = {
  name: "ribeiro.dev",
  websiteUrl: "https://ribeiro.dev",
  cnpj: "64.317.454/0001-73",
  email: "g@ribeiro.dev"
};

// src/value-objects/plan.value-object.ts
import { z as z9 } from "zod";
var PLANS = {
  FREE: "free",
  PRO_MONTHLY: "pro_monthly",
  PRO_YEARLY: "pro_yearly"
};
var PLAN_SCHEMA = z9.enum([
  "free",
  "pro_monthly",
  "pro_yearly"
]);
var SUBSCRIPTION_STATUSES = {
  ACTIVE: "active",
  CANCELED: "canceled"
};
var SUBSCRIPTION_STATUS_SCHEMA = z9.enum([
  "active",
  "canceled"
]);
var PLAN_LIMITS = {
  [PLANS.FREE]: {
    maxRelationships: 1,
    maxWishlistItems: 3,
    maxOccasions: 3
  },
  [PLANS.PRO_MONTHLY]: {
    maxRelationships: 1,
    maxWishlistItems: Infinity,
    maxOccasions: Infinity
  },
  [PLANS.PRO_YEARLY]: {
    maxRelationships: 1,
    maxWishlistItems: Infinity,
    maxOccasions: Infinity
  }
};

// src/dtos/internal/subscription.dto.ts
var CREATE_SUBSCRIPTION_INPUT_SCHEMA = z10.object({
  userId: z10.string().uuid(),
  plan: PLAN_SCHEMA.optional()
});

// src/dtos/internal/user.dto.ts
import { z as z11 } from "zod";
var CREATE_USER_INPUT_SCHEMA = z11.object({
  email: z11.string().email(),
  name: z11.string().optional(),
  avatarUrl: z11.string().url().optional()
});
var UPDATE_USER_INPUT_SCHEMA = z11.object({
  name: z11.string().optional(),
  avatarUrl: z11.string().url().optional()
});

// src/dtos/internal/wishlist-item.dto.ts
import { z as z12 } from "zod";
var CREATE_WISHLIST_ITEM_INPUT_SCHEMA = z12.object({
  userId: z12.string().uuid(),
  relationshipId: z12.string().uuid(),
  title: z12.string().min(1),
  description: z12.string().optional(),
  link: z12.string().url().optional()
});
var UPDATE_WISHLIST_ITEM_INPUT_SCHEMA = z12.object({
  title: z12.string().min(1).optional(),
  description: z12.string().optional(),
  link: z12.string().url().optional()
});

// src/dtos/invite/accept-invite.dto.ts
import { z as z13 } from "zod";
var ACCEPT_INVITE_REQUEST_SCHEMA = z13.object({
  invite_token: z13.string(),
  confirm: z13.boolean().optional(),
  // Optional fields for future use
  name: z13.string().min(1).max(100).optional(),
  dates: z13.array(z13.object({
    title: z13.string().min(1).max(100),
    date: z13.string().regex(/^\d{4}-\d{2}-\d{2}$/)
  })).max(10).optional(),
  wishlist_items: z13.array(z13.object({
    title: z13.string().min(1).max(100),
    description: z13.string().max(500).optional(),
    link: z13.string().url().optional()
  })).max(20).optional()
});
var ACCEPT_INVITE_RESPONSE_SCHEMA = z13.discriminatedUnion("status", [
  z13.object({
    status: z13.literal("success")
  }),
  z13.object({
    status: z13.literal("requires_confirmation"),
    currentPartnerName: z13.string(),
    newPartnerName: z13.string()
  })
]);

// src/dtos/invite/validate-invite.dto.ts
import { z as z14 } from "zod";
var VALIDATE_INVITE_RESPONSE_SCHEMA = z14.object({
  creatorName: z14.string()
});

// src/dtos/occasions/create-occasion.dto.ts
import { z as z15 } from "zod";
var CREATE_OCCASION_REQUEST_SCHEMA = z15.object({
  title: z15.string().min(1),
  date: z15.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
});

// src/dtos/occasions/update-occasion.dto.ts
import { z as z16 } from "zod";
var UPDATE_OCCASION_REQUEST_SCHEMA = z16.object({
  title: z16.string().min(1).optional(),
  date: z16.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format").optional()
});

// src/dtos/relationship/switch-relationship.dto.ts
import { z as z17 } from "zod";
var SWITCH_RELATIONSHIP_REQUEST_SCHEMA = z17.object({
  inviteToken: z17.string()
});

// src/dtos/user/check-user-exists.dto.ts
import { z as z18 } from "zod";
var CHECK_USER_EXISTS_RESPONSE_SCHEMA = z18.object({
  exists: z18.boolean()
});

// src/dtos/user/update-user.dto.ts
import { z as z19 } from "zod";
var UPDATE_USER_REQUEST_SCHEMA = z19.object({
  name: z19.string().min(1).max(100).optional(),
  avatar_url: z19.string().url().optional()
});

// src/dtos/wishlist/create-wishlist-item.dto.ts
import { z as z20 } from "zod";
var CREATE_WISHLIST_ITEM_REQUEST_SCHEMA = z20.object({
  title: z20.string().min(1),
  description: z20.string().optional(),
  link: z20.string().url().optional()
});

// src/dtos/wishlist/update-wishlist-item.dto.ts
import { z as z21 } from "zod";
var UPDATE_WISHLIST_ITEM_REQUEST_SCHEMA = z21.object({
  title: z21.string().min(1).optional(),
  description: z21.string().optional(),
  link: z21.string().url().optional()
});

// src/entities/occasion.entity.ts
import { z as z22 } from "zod";
var OCCASION_SCHEMA = z22.object({
  id: z22.string().uuid(),
  userId: z22.string().uuid(),
  relationshipId: z22.string().uuid(),
  title: z22.string(),
  date: z22.string(),
  createdAt: z22.date(),
  updatedAt: z22.date()
});

// src/entities/relationship.entity.ts
import { z as z23 } from "zod";
var RELATIONSHIP_SCHEMA = z23.object({
  id: z23.string().uuid(),
  creatorUserId: z23.string().uuid(),
  guestUserId: z23.string().uuid().nullable(),
  inviteToken: z23.string(),
  deletedAt: z23.date().nullable(),
  createdAt: z23.date()
});

// src/entities/subscription.entity.ts
import { z as z24 } from "zod";
var SUBSCRIPTION_SCHEMA = z24.object({
  id: z24.string().uuid(),
  userId: z24.string().uuid(),
  plan: PLAN_SCHEMA,
  status: SUBSCRIPTION_STATUS_SCHEMA,
  createdAt: z24.date(),
  updatedAt: z24.date()
});

// src/entities/user.entity.ts
import { z as z25 } from "zod";
var USER_SCHEMA = z25.object({
  id: z25.string().uuid(),
  email: z25.string().email(),
  name: z25.string().nullable(),
  avatarUrl: z25.string().url().nullable(),
  createdAt: z25.date(),
  updatedAt: z25.date()
});

// src/entities/wishlist-item.entity.ts
import { z as z26 } from "zod";
var WISHLIST_ITEM_SCHEMA = z26.object({
  id: z26.string().uuid(),
  userId: z26.string().uuid(),
  relationshipId: z26.string().uuid(),
  title: z26.string(),
  description: z26.string().nullable(),
  link: z26.string().url().nullable(),
  createdAt: z26.date(),
  updatedAt: z26.date()
});
export {
  ACCEPT_INVITE_REQUEST_SCHEMA,
  ACCEPT_INVITE_RESPONSE_SCHEMA,
  AUTH_TOKEN_SCHEMA,
  AUTH_TOKEN_TYPE,
  CHECK_USER_EXISTS_RESPONSE_SCHEMA,
  COMPANY,
  CREATE_AUTH_TOKEN_INPUT_SCHEMA,
  CREATE_OCCASION_INPUT_SCHEMA,
  CREATE_OCCASION_REQUEST_SCHEMA,
  CREATE_RELATIONSHIP_INPUT_SCHEMA,
  CREATE_SUBSCRIPTION_INPUT_SCHEMA,
  CREATE_USER_INPUT_SCHEMA,
  CREATE_WISHLIST_ITEM_INPUT_SCHEMA,
  CREATE_WISHLIST_ITEM_REQUEST_SCHEMA,
  LOGIN_CONFIRM_REQUEST_SCHEMA,
  LOGIN_CONFIRM_RESPONSE_SCHEMA,
  LOGIN_REQUEST_SCHEMA,
  LOGIN_RESPONSE_SCHEMA,
  OCCASION_SCHEMA,
  PLANS,
  PLAN_LIMITS,
  PLAN_SCHEMA,
  PRE_REGISTRATION_METADATA_SCHEMA,
  PRE_REGISTRATION_REQUEST_SCHEMA,
  REFRESH_TOKEN_REQUEST_SCHEMA,
  REFRESH_TOKEN_RESPONSE_SCHEMA,
  RELATIONSHIP_SCHEMA,
  SUBSCRIPTION_SCHEMA,
  SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_STATUS_SCHEMA,
  SWITCH_RELATIONSHIP_REQUEST_SCHEMA,
  UPDATE_OCCASION_INPUT_SCHEMA,
  UPDATE_OCCASION_REQUEST_SCHEMA,
  UPDATE_USER_INPUT_SCHEMA,
  UPDATE_USER_REQUEST_SCHEMA,
  UPDATE_WISHLIST_ITEM_INPUT_SCHEMA,
  UPDATE_WISHLIST_ITEM_REQUEST_SCHEMA,
  USER_IDENTIFIER_TYPE,
  USER_SCHEMA,
  VALIDATE_INVITE_RESPONSE_SCHEMA,
  WISHLIST_ITEM_SCHEMA
};
//# sourceMappingURL=index.js.map